import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@egaranti/components";

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import statsService from "@/services/statsService";

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";

import ExportIcon from "@/assets/icons/export.svg?react";

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

const WarrantyChartSection = () => {
  const { t, ready } = useTranslation();
  const [data, setData] = useState({
    labels: [],
    datasets: [],
  });
  const [selectedDate, setSelectedDate] = useState("4");
  const [loading, setLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const intervalSettings = useMemo(
    () => ({
      1: { interval: 7, temporalUnitEnum: "DAY" },
      2: { interval: 1, temporalUnitEnum: "MONTH" },
      3: { interval: 3, temporalUnitEnum: "MONTH" },
      4: { interval: 6, temporalUnitEnum: "MONTH" },
      5: { interval: 1, temporalUnitEnum: "YEAR" },
    }),
    [],
  );

  const getBackgroundColor = useMemo(
    () => (label) => {
      const colors = {
        "overviewPage.product": "#0037AD",
        "overviewPage.productfromintegration": "#6CE9A6",
      };
      return colors[label] || "#000000";
    },
    [],
  );

  const transformData = useMemo(
    () => (backendData) => {
      if (!backendData || !backendData.labels || !backendData.datasets) {
        return {
          dateIntervalString: "",
          chartData: [],
          chartConfig: {},
          datasets: [],
        };
      }

      const chartData = backendData.labels.map((label, index) => {
        const dataPoint = { name: label };
        backendData.datasets.forEach((dataset) => {
          const translatedLabel = t(dataset.label, {
            defaultValue: dataset.label,
          });
          dataPoint[translatedLabel] = dataset.counts[index] || 0;
        });
        return dataPoint;
      });

      const chartConfig = {};
      backendData.datasets.forEach((dataset) => {
        const translatedLabel = t(dataset.label, {
          defaultValue: dataset.label,
        });
        chartConfig[translatedLabel] = {
          label: translatedLabel,
          color: getBackgroundColor(dataset.label),
        };
      });

      return {
        dateIntervalString: backendData.dateIntervalString || "",
        chartData,
        chartConfig,
        datasets: backendData.datasets.map((dataset) => ({
          key: t(dataset.label, { defaultValue: dataset.label }),
          color: getBackgroundColor(dataset.label),
        })),
      };
    },
    [t, getBackgroundColor],
  );

  const fetchData = async () => {
    if (!ready) return;

    setLoading(true);

    const { interval, temporalUnitEnum } = intervalSettings[selectedDate] || {
      interval: 7,
      temporalUnitEnum: "DAY",
    };

    try {
      const response = await statsService.getSendedWarranties({
        interval,
        temporalUnitEnum,
      });

      if (response && response.data) {
        setData(transformData(response.data));
      }
    } catch (error) {
      console.error("Error fetching warranty data:", error);

      setData({
        dateIntervalString: "",
        chartData: [],
        chartConfig: {},
        datasets: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const onExport = async () => {
    setIsSent(true);

    const { interval, temporalUnitEnum } = intervalSettings[selectedDate] || {
      interval: 7,
      temporalUnitEnum: "DAY",
    };

    try {
      const response = await statsService.exportWarranties({
        interval,
        temporalUnitEnum,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "warranty.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting warranties:", error);
    } finally {
      setIsSent(false);
    }
  };

  useEffect(() => {
    if (ready) {
      fetchData();
    }
  }, [selectedDate, ready]);

  if (!ready) {
    return (
      <div className="flex h-[400px] items-center justify-center text-[#677389]">
        {t("common.loading", { defaultValue: "Loading translations..." })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        EPS-5120 (4. bulgu) — ustteki satir mobilde sigmiyordu: baslik, tarih
        araligi, donem secimi ve "Disa Aktar" tek bir flex satirinda sikisiyor,
        tarih araligi 5 satira boluniyor, secim "So..." diye kesiliyor, dugme
        iki satira dusuyordu (replikada olculdu: 390 px'te tarih 5 satir /
        100 px, secim 101 px, dugme 2 satir).

        Cozum: dar ekranda alt alta, sm'den itibaren eski tek satir duzeni.
      */}
      <header className="flex w-full flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
        <h3 className="text-lg font-medium text-[#111729]">
          {t("overviewPage.warrantySendings", {
            defaultValue: "Warranty Sendings",
          })}
        </h3>
        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto sm:flex-nowrap sm:gap-4">
          <span className="text-sm font-medium text-[#677389]">
            {data.dateIntervalString}
          </span>
          <div className="bg-white">
            <Select onValueChange={setSelectedDate} value={selectedDate}>
              <SelectTrigger>
                <SelectValue
                  placeholder={t("overviewPage.selectDate", {
                    defaultValue: "Select Date",
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">
                  {t("date.lastWeek", { defaultValue: "Last Week" })}
                </SelectItem>
                <SelectItem value="2">
                  {t("date.lastMonth", { defaultValue: "Last Month" })}
                </SelectItem>
                <SelectItem value="3">
                  {t("date.last3Months", { defaultValue: "Last 3 Months" })}
                </SelectItem>
                <SelectItem value="4">
                  {t("date.last6Months", { defaultValue: "Last 6 Months" })}
                </SelectItem>
                <SelectItem value="5">
                  {t("date.lastYear", { defaultValue: "Last Year" })}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button disabled={isSent} variant="secondaryGray" onClick={onExport}>
            <ExportIcon />
            {t("common.export", { defaultValue: "Export" })}
          </Button>
        </div>
      </header>
      {/*
        EPS-5120 (4. bulgu) — yan dolgu her ekranda sabit `px-[120px]` idi.
        390 px'lik ekranda grafige yalnizca 116 px kaliyor, cubuklar ince cizgi
        gibi gorunuyordu (replikada olculdu: svg 116x400). Masaustunde 932 px ile
        sorun yok, bu yuzden 120 px dolgu `sm:`den itibaren aynen korunuyor.
      */}
      <section className="rounded-lg border bg-white px-4 py-6 sm:px-[120px] sm:py-9">
        {data.chartData && data.chartData.length > 0 ? (
          <ChartContainer
            config={data.chartConfig || {}}
            className="h-[400px] w-full"
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                {data.datasets?.map((dataset) => (
                  <Bar
                    key={dataset.key}
                    dataKey={dataset.key}
                    fill={dataset.color}
                    radius={[8, 8, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        ) : (
          <div className="flex h-[400px] items-center justify-center text-[#677389]">
            {loading
              ? t("common.loading", { defaultValue: "Veri yükleniyor..." })
              : t("common.noData", { defaultValue: "Veri bulunamadı" })}
          </div>
        )}
      </section>
    </div>
  );
};

export default WarrantyChartSection;
